import React, { useMemo, useState, useCallback, memo } from "react";
import {
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Avatar,
  Tooltip,
  Collapse,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Business,
  FolderOpenOutlined,
  Videocam,
  DescriptionOutlined,
  Article,
  Analytics,
  Email,
  Phone,
  Language,
  LocationOn,
  ExpandMore,
  ExpandLess,
  Add,
  OpenInNew,
  ChevronRight,
  Edit,
  NoteAdd,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { formatDate, formatRelativeTime } from "../utils/dateUtils";
import { openDocumentEditor, projectPath, sessionPath } from "../utils/navigation";
import EntityBreadcrumbs from "./layout/EntityBreadcrumbs";
import { CustomerFormFields } from "./CustomerList";
import {
  PageContainer,
  PageHeader,
  SectionCard,
  EmptyState,
  ListSkeleton,
  IconTile,
} from "./pagekit";

/** Reasonable email shape: no spaces, one @, and a dot in the domain. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Phone: digits/spaces and + - ( ) / . only, with at least one digit. */
const PHONE_REGEX = /^(?=.*\d)[\d\s+\-()./]+$/;
/** Bare domain shape, e.g. "example.com" (labels separated by dots). */
const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/**
 * Whether a project has anything the generator can work from. Same rule as the document
 * generator: requirements, sessions or existing documents (usable as a reference) all count.
 * @param {object} project - Project row from app state.
 * @returns {boolean}
 */
const projectHasSources = (project) =>
  (project.requirements?.length || 0)
    + (project.sessions?.length || 0)
    + (project.generatedDocuments?.length || 0) > 0;

/**
 * Validate an email value.
 * @param {string} value - Raw input.
 * @returns {string} Error message, or "" when valid/empty.
 */
const getEmailError = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return EMAIL_REGEX.test(trimmed) ? "" : "Enter a valid email address";
};

/**
 * Validate a phone value.
 * @param {string} value - Raw input.
 * @returns {string} Error message, or "" when valid/empty.
 */
const getPhoneError = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return PHONE_REGEX.test(trimmed)
    ? ""
    : "Phone may only contain digits, spaces, and + - ( ) / .";
};

/**
 * Validate a website value. Only http(s) URLs are allowed; bare domains
 * are accepted (treated as https). Other schemes (e.g. javascript:) are rejected.
 * @param {string} value - Raw input.
 * @returns {string} Error message, or "" when valid/empty.
 */
const getWebsiteError = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (!/^https?:\/\//i.test(trimmed) && /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return "Website must use http:// or https://";
  }
  const host = trimmed
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split(/[/?#]/)[0];
  return DOMAIN_REGEX.test(host.toLowerCase())
    ? ""
    : "Enter a valid website (e.g. https://example.com)";
};

/**
 * Normalize a valid website value for saving: prefix bare domains with https://.
 * @param {string} value - Raw input.
 * @returns {string} Normalized website URL or "".
 */
const normalizeWebsite = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * Normalize a raw domain string by stripping protocol, www, and trailing slashes.
 * @param {string} raw - The raw domain input.
 * @returns {string} Cleaned domain string.
 */
const normalizeDomain = (raw) => {
  let d = raw.trim().toLowerCase();
  d = d.replace(/^https?:\/\//, "");
  d = d.replace(/^www\./, "");
  d = d.replace(/\/+$/, "");
  return d;
};

/**
 * Customer Dashboard page showing projects, sessions, and documents
 * for a specific customer in a hierarchical dashboard layout.
 * @returns {JSX.Element} Customer dashboard view.
 */
const CustomerDashboard = memo(() => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [expandedProjects, setExpandedProjects] = useState({});

  // Create project dialog state
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
    name: "",
    description: "",
  });

  // Edit customer dialog state
  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    name: "",
    description: "",
    contact_email: "",
    contact_phone: "",
    company_website: "",
    address: "",
    domain: [],
  });
  const [domainInput, setDomainInput] = useState("");
  const [domainError, setDomainError] = useState("");

  /** Inline validation errors for the customer contact fields. */
  const customerFieldErrors = useMemo(
    () => ({
      contact_email: getEmailError(customerFormData.contact_email),
      contact_phone: getPhoneError(customerFormData.contact_phone),
      company_website: getWebsiteError(customerFormData.company_website),
    }),
    [
      customerFormData.contact_email,
      customerFormData.contact_phone,
      customerFormData.company_website,
    ]
  );
  const hasCustomerFieldErrors = Object.values(customerFieldErrors).some(
    Boolean
  );

  /** @type {object|null} The customer object from app state. */
  const customer = useMemo(
    () => state.customers?.find((c) => c.id === customerId) || null,
    [state.customers, customerId]
  );

  /** @type {Array<object>} Projects belonging to this customer. */
  const customerProjectsList = useMemo(
    () =>
      state.projects?.filter((p) => p.customer_id === customerId) || [],
    [state.projects, customerId]
  );

  /** @type {Array<object>} All sessions aggregated across customer projects. */
  const allSessions = useMemo(() => {
    const sessions = [];
    customerProjectsList.forEach((project) => {
      (project.sessions || []).forEach((session) => {
        sessions.push({ ...session, projectName: project.name, projectId: project.id });
      });
    });
    return sessions.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [customerProjectsList]);

  /** @type {Array<object>} All documents aggregated across customer projects. */
  const allDocuments = useMemo(() => {
    const docs = [];
    customerProjectsList.forEach((project) => {
      (project.generatedDocuments || []).forEach((doc) => {
        docs.push({ ...doc, projectName: project.name, projectId: project.id });
      });
    });
    return docs.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [customerProjectsList]);

  /** @type {number} Total requirements across all projects. */
  const totalRequirements = useMemo(
    () =>
      customerProjectsList.reduce(
        (sum, p) => sum + (p.requirements?.length || 0),
        0
      ),
    [customerProjectsList]
  );

  /** @type {boolean} True when at least one project has requirements, sessions or documents. */
  const canGenerate = useMemo(
    () => customerProjectsList.some(projectHasSources),
    [customerProjectsList]
  );

  /**
   * Toggle the expanded state for a project's session list.
   * @param {string} projectId - The project ID to toggle.
   */
  const toggleProject = useCallback((projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  }, []);

  /**
   * Open the edit customer dialog, pre-filling form data.
   */
  const handleOpenEditCustomer = useCallback(() => {
    if (!customer) return;
    setCustomerFormData({
      name: customer.name || "",
      description: customer.description || "",
      contact_email: customer.contact_email || "",
      contact_phone: customer.contact_phone || "",
      company_website: customer.company_website || "",
      address: customer.address || "",
      domain: customer.domain || [],
    });
    setDomainInput("");
    setDomainError("");
    setEditCustomerDialogOpen(true);
  }, [customer]);

  /**
   * Save the edited customer details.
   */
  const handleSaveCustomer = useCallback(() => {
    if (customerFormData.name.trim() && customer && !hasCustomerFieldErrors) {
      // Commit any valid pending domain text so it isn't silently lost
      let domains = customerFormData.domain;
      if (domainInput.trim()) {
        const cleaned = normalizeDomain(domainInput);
        if (!DOMAIN_REGEX.test(cleaned)) {
          setDomainError("Enter a valid domain (e.g. example.com)");
          return;
        }
        if (!domains.includes(cleaned)) {
          domains = [...domains, cleaned];
        }
      }
      actions.updateCustomer(customer.id, {
        ...customerFormData,
        company_website: normalizeWebsite(customerFormData.company_website),
        domain: domains,
      });
      setDomainInput("");
      setDomainError("");
      setEditCustomerDialogOpen(false);
    }
  }, [
    actions,
    customer,
    customerFormData,
    hasCustomerFieldErrors,
    domainInput,
  ]);

  /**
   * Add a domain chip from the current domainInput value.
   * Shows an inline error when the input is not a valid domain.
   */
  const handleAddDomain = useCallback(() => {
    if (!domainInput.trim()) return;
    const cleaned = normalizeDomain(domainInput);
    if (!DOMAIN_REGEX.test(cleaned)) {
      setDomainError("Enter a valid domain (e.g. example.com)");
      return;
    }
    setDomainError("");
    if (customerFormData.domain.includes(cleaned)) {
      setDomainInput("");
      return;
    }
    setCustomerFormData((prev) => ({
      ...prev,
      domain: [...prev.domain, cleaned],
    }));
    setDomainInput("");
  }, [domainInput, customerFormData.domain]);

  /**
   * Remove a domain chip by value.
   * @param {string} domainToRemove - The domain to remove.
   */
  const handleRemoveDomain = useCallback((domainToRemove) => {
    setCustomerFormData((prev) => ({
      ...prev,
      domain: prev.domain.filter((d) => d !== domainToRemove),
    }));
  }, []);

  /**
   * Create a new project under this customer.
   */
  const handleCreateProject = useCallback(() => {
    if (projectFormData.name.trim() && customerId) {
      actions.createProject({
        ...projectFormData,
        customer_id: customerId,
      });
      setProjectFormData({ name: "", description: "" });
      setCreateProjectDialogOpen(false);
    }
  }, [actions, projectFormData, customerId]);

  // Loading state
  if (!state.initialLoadComplete || state.loading) {
    return (
      <PageContainer aria-busy="true" aria-label="Loading customer">
        <Skeleton variant="text" width={200} />
        <Skeleton variant="text" width="40%" height={44} />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={84} sx={{ borderRadius: "var(--q-radius-card)" }} />
          ))}
        </Box>
        <SectionCard flush>
          <ListSkeleton rows={4} label="Loading projects" />
        </SectionCard>
      </PageContainer>
    );
  }

  // Customer not found
  if (!customer) {
    return (
      <PageContainer width="narrow">
        <SectionCard>
          <EmptyState
            icon={<Business />}
            title="Customer not found"
            description="It may have been deleted, or you may not have access to it."
            actions={
              <Button variant="contained" onClick={() => navigate("/customers")}>
                All customers
              </Button>
            }
          />
        </SectionCard>
      </PageContainer>
    );
  }

  const contactItems = [
    customer.contact_email && { icon: <Email fontSize="small" />, label: customer.contact_email, href: `mailto:${customer.contact_email}` },
    customer.contact_phone && { icon: <Phone fontSize="small" />, label: customer.contact_phone, href: `tel:${customer.contact_phone.replace(/[^\d+]/g, "")}` },
    customer.company_website && /^https?:\/\//i.test(customer.company_website) && {
      icon: <Language fontSize="small" />,
      label: customer.company_website.replace(/^https?:\/\//i, ""),
      href: customer.company_website,
      external: true,
    },
    customer.address && { icon: <LocationOn fontSize="small" />, label: customer.address },
  ].filter(Boolean);

  const stats = [
    { label: "Projects", value: customerProjectsList.length, icon: <FolderOpenOutlined /> },
    { label: "Sessions", value: allSessions.length, icon: <Videocam /> },
    { label: "Requirements", value: totalRequirements, icon: <Analytics /> },
    { label: "Documents", value: allDocuments.length, icon: <DescriptionOutlined /> },
  ];

  const handleGenerate = () => {
    const params = new URLSearchParams({ customerId: customer.id });
    const [onlyProject] = customerProjectsList;
    if (customerProjectsList.length === 1 && projectHasSources(onlyProject)) params.set("projectId", onlyProject.id);
    navigate(`/generate?${params.toString()}`);
  };

  const rowSx = {
    all: "unset",
    boxSizing: "border-box",
    width: "100%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    px: { xs: 1.5, sm: 2 },
    py: 1.25,
    "&:hover": { bgcolor: "var(--q-bg-subtle)" },
    "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
  };

  return (
    <PageContainer>
      <PageHeader
        breadcrumbs={<EntityBreadcrumbs customerId={customer.id} />}
        leading={
          <Avatar
            variant="rounded"
            sx={{ width: 44, height: 44, bgcolor: "var(--q-accent-soft)", color: "var(--q-accent-text)", fontSize: "1.125rem", fontWeight: 700 }}
          >
            {customer.name?.charAt(0)?.toUpperCase() || "C"}
          </Avatar>
        }
        title={customer.name}
        description={customer.description}
        actions={
          <>
            <Button variant="outlined" startIcon={<Edit />} onClick={handleOpenEditCustomer}>
              Edit
            </Button>
            <Button variant="outlined" startIcon={<Add />} onClick={() => setCreateProjectDialogOpen(true)}>
              New project
            </Button>
            <Button variant="contained" startIcon={<NoteAdd />} onClick={handleGenerate} disabled={!canGenerate}>
              Generate document
            </Button>
          </>
        }
        meta={contactItems.length ? contactItems.map((item) => (
          <Box key={item.label} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: "text.secondary", minWidth: 0 }}>
            {item.icon}
            {item.href ? (
              <MuiLink
                href={item.href}
                underline="hover"
                color="inherit"
                variant="body2"
                {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                sx={{ overflowWrap: "anywhere" }}
              >
                {item.label}
              </MuiLink>
            ) : (
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{item.label}</Typography>
            )}
          </Box>
        )) : null}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5 }}>
        {stats.map((stat) => (
          <Box
            key={stat.label}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: "var(--q-radius-card, 16px)",
            }}
          >
            <IconTile tone="neutral" size={36}>{stat.icon}</IconTile>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" component="div" sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Projects */}
      <SectionCard
        flush
        title="Projects"
        actions={
          <Button size="small" startIcon={<Add />} onClick={() => setCreateProjectDialogOpen(true)}>
            New project
          </Button>
        }
      >
        {customerProjectsList.length === 0 ? (
          <EmptyState
            compact
            icon={<FolderOpenOutlined />}
            title="No projects yet"
            description={`Create a project for ${customer.name} to start capturing meetings and requirements.`}
            actions={
              <Button variant="contained" startIcon={<Add />} onClick={() => setCreateProjectDialogOpen(true)}>
                New project
              </Button>
            }
          />
        ) : (
          <Box component="ul" sx={{ m: 0, p: 0 }}>
            {customerProjectsList.map((project) => (
              <Box component="li" key={project.id} sx={{ listStyle: "none", borderBottom: 1, borderColor: "var(--q-row-divider)", "&:last-of-type": { borderBottom: 0 } }}>
                <Box component="button" type="button" onClick={() => navigate(projectPath(project.id))} sx={rowSx}>
                  <IconTile tone="accent" size={32}><FolderOpenOutlined /></IconTile>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{project.name}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div" noWrap>
                      {project.description || `Created ${formatDate(project.createdAt)}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0.75 }}>
                    <Chip size="small" label={`${project.sessions?.length || 0} sessions`} />
                    <Chip size="small" label={`${project.requirements?.length || 0} requirements`} />
                    <Chip size="small" label={`${project.generatedDocuments?.length || 0} documents`} />
                  </Box>
                  <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </SectionCard>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, alignItems: "start" }}>
        {/* Sessions */}
        <SectionCard flush title={`Sessions${allSessions.length ? ` (${allSessions.length})` : ""}`}>
          {allSessions.length === 0 ? (
            <EmptyState
              compact
              icon={<Videocam />}
              title="No sessions yet"
              description="Sessions live inside projects. Open a project to upload a meeting or import one."
            />
          ) : (
            customerProjectsList
              .filter((p) => (p.sessions?.length || 0) > 0)
              .map((project) => {
                const isExpanded = expandedProjects[project.id] !== false; // default expanded
                return (
                  <Box key={project.id} sx={{ borderBottom: 1, borderColor: "var(--q-border-subtle)", "&:last-of-type": { borderBottom: 0 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: { xs: 1, sm: 1.5 }, py: 0.75, bgcolor: "var(--q-bg-subtle)" }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleProject(project.id)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? `Hide sessions of ${project.name}` : `Show sessions of ${project.name}`}
                      >
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                      <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap>{project.name}</Typography>
                      <Chip size="small" label={project.sessions.length} />
                      <Tooltip title="Open project sessions">
                        <IconButton size="small" aria-label={`Open sessions of ${project.name}`} onClick={() => navigate(projectPath(project.id, "sessions"))}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Collapse in={isExpanded}>
                      <Box component="ul" sx={{ m: 0, p: 0 }}>
                        {project.sessions.map((session) => (
                          <Box component="li" key={session.id} sx={{ listStyle: "none", borderTop: 1, borderColor: "var(--q-row-divider)" }}>
                            <Box component="button" type="button" onClick={() => navigate(sessionPath(session.id))} sx={rowSx}>
                              <Videocam fontSize="small" sx={{ color: "text.secondary" }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{session.name}</Typography>
                                {session.summary && (
                                  <Typography variant="caption" color="text.secondary" noWrap component="div">{session.summary}</Typography>
                                )}
                              </Box>
                              {session.source === "fathom" && <Chip label="Fathom" size="small" color="info" />}
                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {formatRelativeTime(session.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })
          )}
        </SectionCard>

        {/* Documents */}
        <SectionCard
          flush
          title={`Documents${allDocuments.length ? ` (${allDocuments.length})` : ""}`}
          actions={
            <Button size="small" startIcon={<NoteAdd />} onClick={handleGenerate} disabled={!canGenerate}>
              Generate
            </Button>
          }
        >
          {allDocuments.length === 0 ? (
            <EmptyState
              compact
              icon={<DescriptionOutlined />}
              title="No documents yet"
              description={canGenerate
                ? "Generate a document from a template using this customer's requirements, sessions or existing documents."
                : "Documents are generated from a project's requirements, sessions or existing documents. Import a session or add requirements first."}
            />
          ) : (
            <Box component="ul" sx={{ m: 0, p: 0 }}>
              {allDocuments.map((doc) => (
                <Box component="li" key={doc.id} sx={{ listStyle: "none", borderBottom: 1, borderColor: "var(--q-row-divider)", "&:last-of-type": { borderBottom: 0 } }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => openDocumentEditor(navigate, { documentId: doc.id, projectId: doc.projectId, title: doc.name })}
                    sx={rowSx}
                  >
                    <Article fontSize="small" sx={{ color: "var(--q-accent-text)" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{doc.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {[doc.projectName, doc.templateName ? `Template: ${doc.templateName}` : null].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                    {doc.requirementsCount > 0 && (
                      <Chip label={`${doc.requirementsCount} reqs`} size="small" sx={{ display: { xs: "none", sm: "inline-flex" } }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatRelativeTime(doc.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>
      </Box>

      {/* Create project */}
      <Dialog
        open={createProjectDialogOpen}
        onClose={() => setCreateProjectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>New project for {customer.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              autoFocus
              label="Project name"
              fullWidth
              value={projectFormData.name}
              onChange={(e) => setProjectFormData({ ...projectFormData, name: e.target.value })}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={3}
              value={projectFormData.description}
              onChange={(e) => setProjectFormData({ ...projectFormData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateProjectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateProject} variant="contained" disabled={!projectFormData.name.trim()}>
            Create project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit customer */}
      <Dialog
        open={editCustomerDialogOpen}
        onClose={() => setEditCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        disableRestoreFocus
      >
        <DialogTitle>Edit customer</DialogTitle>
        <DialogContent>
          <CustomerFormFields
            formData={customerFormData}
            setFormData={setCustomerFormData}
            fieldErrors={customerFieldErrors}
            domainInput={domainInput}
            setDomainInput={setDomainInput}
            domainError={domainError}
            setDomainError={setDomainError}
            onAddDomain={handleAddDomain}
            onRemoveDomain={handleRemoveDomain}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCustomerDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveCustomer}
            variant="contained"
            disabled={!customerFormData.name.trim() || hasCustomerFieldErrors}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
});

CustomerDashboard.displayName = "CustomerDashboard";

export default CustomerDashboard;
